"use client";

import { useEffect, useMemo, useState } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import {
  useAprovarFaturaStore,
  CondicaoDialogFaturas,
  useFinanceiroAprovarFaturasMutation,
  AprovarFaturaBody,
} from "@FN";
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, fmt } from "visuais";
import { Input, Label } from "visuais";
import { useUserStore } from "@/_nucleo/store";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

interface AprovarFaturaModalProps {
  isOpen: boolean;
  onClose: () => void;
  fatura: Fatura;
}

export function AprovarFaturaModal({ isOpen, onClose, fatura }: AprovarFaturaModalProps) {
  const queryClient = useQueryClient();
  const resetPrenota = usePrenotaStore((state) => state.actions.reset);
  const faturaStore = useAprovarFaturaStore((state) => state);
  const usr = useUserStore((state) => state.user?.login || "");

  const [numtit, setNumtitState] = useState("");

  // Mutation de aprovação usando o REC da fatura
  const aprovarMutation = useFinanceiroAprovarFaturasMutation(fatura.R_E_C_N_O_, usr);
  
  useEffect(() => {
    if (isOpen) {
      resetPrenota();
      setNumtitState(""); // limpa o título ao abrir
    }
  }, [isOpen, resetPrenota]);
  
  const canFinalize = useMemo(() => {
    return !!numtit && !!fatura.Z15_STATUS && !!faturaStore.fatura.cond.E4_CODIGO;
  }, [numtit, fatura, faturaStore.fatura.cond.E4_CODIGO]);

  // Evita shift de fuso: converte "YYYY-MM-DD" para Date local
  function parseLocalDate(input: string | Date): Date {
    if (input instanceof Date) return input;
    const s = String(input || "");
    const m = s.match(/^\d{4}-\d{2}-\d{2}$/);
    if (m) {
      const [y, mo, d] = s.split("-").map(Number);
      return new Date(y, (mo || 1) - 1, d || 1);
    }
    // fallback
    return new Date(s);
  }

  const handleFinalizar = async () => {
    if (!canFinalize) return;

    try {
      const aprovarBody: AprovarFaturaBody = {
        numtit,
        cond: faturaStore.fatura.cond,
        vencimentos: faturaStore.fatura.vencimentos.map(v => ({
          ...v,
          Vencimento: fmt.dates.BR(parseLocalDate(v.Vencimento)), // DD/MM/YYYY sem shift
        })),      
      };
      
      const data = await aprovarMutation.mutateAsync(aprovarBody);
      
      if (data.Sucesso === false) {
        toast.error(data.Mensagem || "Erro ao aprovar fatura");
        console.error(data);
      } else {
        toast.success("Fatura aprovada com sucesso");
        queryClient.invalidateQueries({ queryKey: ["financeiro", "faturas"] });
      }

      onClose();
      resetPrenota();
    } catch (err) {
      console.error("Erro ao aprovar fatura:", err);
      toast.error("Erro ao aprovar fatura: " + (err as Error).message);
    }
  };

  const isProcessing = aprovarMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose} key={fatura.R_E_C_N_O_}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Detalhes do Pagamento</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col space-y-1">
              <Label htmlFor="titulo">Título</Label>
              <Input
                id="titulo"
                placeholder="Número do título"
                value={numtit}
                onChange={(e) => setNumtitState(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Valor total:</span>
              <Badge variant="secondary" className="bg-emerald-800 border border-emerald-500 font-bold text-base text-white">
                {fmt.numbers.currency(fatura.Z15_VALOR)}
              </Badge>
            </div>
            
            <div className="flex flex-col space-y-1">
              <Label htmlFor="condicao">Cond. Pagamento</Label>
              <CondicaoDialogFaturas fatura={fatura}/>
            </div>
          </div>

          <div className="flex items-center">
            <Button
              className="bg-emerald-800 border border-emerald-500 mr-2 hover:bg-emerald-700"
              disabled={!canFinalize || isProcessing}
              onClick={handleFinalizar}
            >
              {isProcessing ? "Finalizando..." : "Finalizar"}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
